import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Trash2, Plus, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => (s || "").trim().toLowerCase();

export default function CategoryBudgetSection({ projectId, readOnly, purchases }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ category: "", allocated: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ category: "", allocated: "" });

  const load = () => api.get(`/projects/${projectId}/budget`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const clientPriceOf = (p) =>
    p.client_price != null ? p.client_price : (p.quantity || 0) * (p.trade_cost || 0) * (1 + (p.markup || 0) / 100);

  // Running total of itemized purchases per category (links purchases -> allocations).
  const spentByCat = (purchases || []).reduce((acc, p) => {
    const k = norm(p.category);
    acc[k] = (acc[k] || 0) + clientPriceOf(p);
    return acc;
  }, {});
  const spentFor = (cat) => spentByCat[norm(cat)] || 0;

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/budget`, {
        label: form.category, category: form.category || "General",
        allocated: parseFloat(form.allocated) || 0, spent: 0,
      });
      setForm({ category: "", allocated: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const startEdit = (i) => { setEditingId(i.id); setEditForm({ category: i.category || "", allocated: i.allocated ?? "" }); };
  const cancelEdit = () => { setEditingId(null); setEditForm({ category: "", allocated: "" }); };

  const saveEdit = async (id) => {
    try {
      await api.put(`/budget/${id}`, {
        label: editForm.category, category: editForm.category || "General",
        allocated: parseFloat(editForm.allocated) || 0,
      });
      cancelEdit();
      load();
      toast.success("Category updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/budget/${id}`); load(); };

  const totalAlloc = items.reduce((s, i) => s + (i.allocated || 0), 0);
  const totalSpent = items.reduce((s, i) => s + spentFor(i.category), 0);
  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-5" data-testid="category-budget-section">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">By Category</p>
        <h3 className="font-serif text-3xl text-[#2B2622]">Category Allocations</h3>
        <p className="text-sm text-[#5C5651] mt-1">Spent updates automatically from itemized purchases filed under each category.</p>
      </div>

      <div className="border border-[#EBE5E0] bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#EBE5E0] text-left text-[11px] uppercase tracking-wider text-[#A68A6B]">
              <th className="p-4 font-medium">Category</th>
              <th className="p-4 font-medium text-right">Allocated</th>
              <th className="p-4 font-medium text-right">Spent</th>
              <th className="p-4 font-medium text-right">Remaining</th>
              {!readOnly && <th className="p-4 w-20"></th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-[#5C5651]">No category allocations yet.</td></tr>
            )}
            {items.map((i) => {
              const spent = spentFor(i.category);
              const rem = (i.allocated || 0) - spent;
              const isEditing = editingId === i.id;
              return (
                <tr key={i.id} className="border-b border-[#F2EFEB] last:border-0" data-testid="category-budget-row">
                  {isEditing ? (
                    <>
                      <td className="p-3">
                        <Input data-testid={`edit-category-name-${i.id}`} value={editForm.category}
                          onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                          className="h-9 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                      </td>
                      <td className="p-3">
                        <Input data-testid={`edit-category-allocated-${i.id}`} type="number" step="0.01" value={editForm.allocated}
                          onChange={(e) => setEditForm({ ...editForm, allocated: e.target.value })}
                          className="h-9 rounded-none border-[#EBE5E0] text-right focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                      </td>
                      <td className="p-4 text-right text-[#9b938b]">{fmt(spent)}</td>
                      <td className="p-4 text-right text-[#9b938b]">—</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button data-testid={`save-category-${i.id}`} onClick={() => saveEdit(i.id)}
                          className="text-[#506B52] hover:text-[#3d523f] mr-3"><Check size={17} strokeWidth={1.75} /></button>
                        <button data-testid={`cancel-category-${i.id}`} onClick={cancelEdit}
                          className="text-[#5C5651] hover:text-[#9E4747]"><X size={17} strokeWidth={1.75} /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-4 text-[#2B2622]">{i.category}</td>
                      <td className="p-4 text-right text-[#2B2622]">{fmt(i.allocated)}</td>
                      <td className="p-4 text-right text-[#2B2622]">{fmt(spent)}</td>
                      <td className="p-4 text-right" style={{ color: rem < 0 ? "#9E4747" : "#506B52" }}>{fmt(rem)}</td>
                      {!readOnly && (
                        <td className="p-4 text-right whitespace-nowrap">
                          <button data-testid={`edit-category-${i.id}`} onClick={() => startEdit(i)}
                            className="text-[#5C5651] hover:text-[#A68A6B] mr-3"><Pencil size={15} strokeWidth={1.5} /></button>
                          <button data-testid={`delete-category-${i.id}`} onClick={() => remove(i.id)}
                            className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              );
            })}
            {items.length > 0 && (
              <tr className="bg-[#F9F8F6] font-medium" data-testid="category-budget-totals">
                <td className="p-4 text-[#2B2622] uppercase text-xs tracking-wider">Total</td>
                <td className="p-4 text-right text-[#2B2622]">{fmt(totalAlloc)}</td>
                <td className="p-4 text-right text-[#2B2622]">{fmt(totalSpent)}</td>
                <td className="p-4 text-right text-[#2B2622]">{fmt(totalAlloc - totalSpent)}</td>
                {!readOnly && <td></td>}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <form onSubmit={add} className="grid sm:grid-cols-3 gap-3 items-end border border-[#EBE5E0] bg-[#F9F8F6] p-4">
          <Input data-testid="category-name-input" placeholder="Category" required value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
          <Input data-testid="category-allocated-input" type="number" step="0.01" placeholder="Allocated" value={form.allocated}
            onChange={(e) => setForm({ ...form, allocated: e.target.value })}
            className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
          <Button data-testid="add-category-btn" type="submit"
            className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs">
            <Plus size={15} className="mr-1" /> Add Category
          </Button>
        </form>
      )}
    </div>
  );
}
